import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FocusOn } from 'react-focus-on';

import { cn } from '@/lib/cn';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<Size, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: Size;
  children: ReactNode;
  footer?: ReactNode;
  /** If false, clicking the backdrop does NOT close the modal. */
  closeOnBackdrop?: boolean;
};

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <FocusOn onEscapeKey={onClose} onClickOutside={closeOnBackdrop ? onClose : undefined}>
        <div
          className={cn(
            'w-full rounded-2xl bg-white shadow-xl',
            SIZES[size]
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {title && (
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
                {title}
              </h2>
            </div>
          )}
          <div className="px-6 py-4">{children}</div>
          {footer && (
            <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-3 rounded-b-2xl">
              {footer}
            </div>
          )}
        </div>
      </FocusOn>
    </div>,
    document.body
  );
}
